import { useEffect, useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import './bookingI18n';
import { IconAlarm, IconAnchor, IconBell, IconCalendar, IconCalendarEvent, IconChecklist, IconChevronDown, IconClock, IconDots, IconDownload, IconFilter, IconKeyboard, IconPlus, IconRefresh, IconSearch, IconSend, IconShip, IconUsersGroup } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useNavigate } from '@tanstack/react-router';
import { HeaderTool, Page, PageHeader, useShell } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { BOOKING_COVER, RL_MAIN, carrierChannel, countdown, rowCutoff, rqStatus, shipStatus, RL_TABS, rlFilter, rlOwnCounts, rlTabCounts, scopeBookings, todayStrip, groupByVessel, type BookingScope, type RlTab } from './booking';
import { BOOKING_P0, BOOKING_VESSELS, BOOKING_VESSEL_ETD } from './fixtures';
import type { Vessel } from '../pricing/types';
import { VesselView } from './VesselView';
import { BookingTwoForm } from './BookingTwoForm';
import { VesselDrawer } from '../pricing/VesselDrawer';
import { BookingPad } from './BookingPad';
import { BookingPadDetail } from './BookingPadDetail';
import { BookingPhone } from './BookingPhone';
import { BookingPhoneDetail } from './BookingPhoneDetail';
import { Dock } from './Dock';
import { InboxPane } from './InboxPane';
import type { BookingRequest } from './types';
import s from './booking.module.css';

// Mint-Booking (desktop 1536): hộp thư info@ · tab RQ · tìm + phạm vi · báo khẩn P0 · việc hôm nay · nhóm theo tàu. Chỉ đọc; nút ghi báo toast.
// Người xem lấy theo board (PHUONGHT, Team BKG); đổi sang người đăng nhập thật cùng lúc chuyển dữ liệu thật (D-06).
// Góc nhìn Tàu: VesselView. Chưa làm: 2 form mở từ dòng, bộ lọc nhiều trường / JobDate, xem thư, bảng SLA.

const ME = 'PHUONGHT';
/** Thứ tự nhóm tàu của board (VORD): ETD gần nhất trước, "Chưa chọn chuyến" cuối. */
const VESSEL_ORDER = ['cma', 'kmtc', 'one', 'ym', 'msc', 'wh', 'none'];
const PHONE_ORDER = ['one', 'ym', 'cma', 'kmtc', 'msc', 'wh', 'none'];

const SCOPES: ReadonlyArray<{ key: BookingScope; label: string; tip: string }> = [
  { key: 'mine', label: 'Của tôi', tip: `BookingTeam = ${ME}` },
  { key: 'cover', label: 'Tôi thay', tip: 'Job của người đang nghỉ mà tôi là backup · thông báo và tag PIC tự chuyển' },
  { key: 'un', label: 'Chưa nhận', tip: 'BookingTeam trống' },
  { key: 'team', label: 'Cả team', tip: 'Cả Team BKG' },
];

const FLAG: Record<BookingRequest['flags'][number], { k: string; tip: string; cls?: string }> = {
  pri: { k: 'Ưu tiên', tip: 'Ưu tiên xử lý', cls: s.pillErr },
  hen: { k: 'Hẹn giờ', tip: 'Khách hẹn giờ lấy booking', cls: s.pillWarn },
  rodp: { k: 'ROD+P', tip: 'Gửi ROD kèm P' },
};
const SOURCE: Record<NonNullable<BookingRequest['sourceStatus']>, { k: string; tip: string }> = {
  cancel: { k: 'Khách huỷ', tip: 'Job đã huỷ ở nguồn · chờ xoá RQ' },
  waitrls: { k: 'Chờ release', tip: 'Hãng giữ release' },
};

const initials = (u: string) => u.slice(0, 2).toUpperCase();

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function BookingList() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { layout } = useShell();
  const { data, error, isLoading, isError, refetch } = useList('booking', { limit: 5000 });
  const forced = readState();
  const [tab, setTab] = useState<RlTab>('all');
  const [scope, setScope] = useState<BookingScope>('mine');
  const [q, setQ] = useState('');
  const [view, setView] = useState<'req' | 'vessel'>('req');
  const [byVessel, setByVessel] = useState(true);
  const [vesselOf, setVesselOf] = useState<string | null>(null);
  const vessels = useList('vessel');
  const profiles = useMemo(() => new Map(((vessels.data?.items ?? []) as Vessel[]).map((v) => [v.id, v])), [vessels.data]);
  const [shut, setShut] = useState<Record<string, true>>({});
  const [inbox, setInbox] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const [dock, setDock] = useState(true);
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  const [ack, setAck] = useState<Record<string, true>>({});

  const all = data?.items ?? [];
  const desktop = layout === 'side' || layout === 'rail';
  const showDock = desktop && wide && dock;
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [detail, setDetail] = useState<string | null>(null);
  const [form, setForm] = useState<string | null>(null);
  const open = (r: BookingRequest) => (layout === 'pad' ? setDetail(r.id) : layout === 'phone' ? setDetail(r.id) : setForm(r.id));

  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // phím [ / ] thu / mở hộp thư (board)
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key === '[') setInbox(false);
      else if (e.key === ']') setInbox(true);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, []);

  const scoped = useMemo(() => scopeBookings(all, { me: ME, scope, q }), [all, scope, q]);
  const counts = useMemo(() => rlTabCounts(scoped), [scoped]);
  const ownCounts = useMemo(() => rlOwnCounts(scopeBookings(all, { me: ME, scope: 'team', q }), ME), [all, q]);
  const prioN = counts.prio;
  const lock = prioN > 0 && !q;
  const tabEff: RlTab = q ? 'all' : lock ? 'prio' : tab;
  const rows = useMemo(() => rlFilter(scoped, tabEff), [scoped, tabEff]);
  const padLayout = layout === 'pad';
  const phoneLayout = layout === 'phone';
  const groups = useMemo(() => ((byVessel && desktop) || padLayout || phoneLayout ? groupByVessel(rows, phoneLayout ? PHONE_ORDER : VESSEL_ORDER) : [{ vessel: '', rows }]), [rows, byVessel, desktop, padLayout, phoneLayout]);
  const viewN = scoped.filter((r) => r.tab !== 'done').length;
  const vesselN = new Set(all.filter((r) => r.vessel !== 'none').map((r) => r.vessel)).size;
  const inMore = !RL_MAIN.includes(tabEff);
  const tabLabel = RL_TABS.find((t) => t.key === tabEff)?.label ?? '';

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const ready = state === 'ready';

  const pick = (k: RlTab) => {
    if (lock && k !== 'prio') return toast({ msg: 'Còn yêu cầu ưu tiên · xử lý ở tab Ưu tiên trước' });
    setTab(k);
    setMoreOpen(false);
  };
  const tabBtn = (k: RlTab, label: string, menu: boolean) => {
    const dis = lock && k !== 'prio';
    const on = tabEff === k;
    return (
      <button
        key={k}
        type="button"
        role={menu ? 'menuitem' : 'tab'}
        aria-selected={menu ? undefined : on}
        aria-disabled={dis}
        title={dis ? `Còn ${prioN} yêu cầu ưu tiên (RQ Cancel / BK Wait RLS) · xử lý tab Ưu tiên trước` : `${label} · ${counts[k]}`}
        className={`${menu ? s.mItem : s.tabBtn} ${on ? s.on : ''} ${dis ? s.dis : ''}`}
        onClick={() => pick(k)}
      >
        {k === 'prio' && prioN > 0 && <span className={s.dotErr} aria-hidden="true" />}
        {label}
        <span className={`${s.tabN} ${k === 'prio' && prioN ? s.tabNErr : ''}`}>{counts[k]}</span>
      </button>
    );
  };

  const viewTabs = (
    <div className={s.hdrTabs}>
      <Segmented
        kind="toggle"
        label="Góc nhìn · phím 1 / 2"
        value={view}
        onChange={(k) => setView(k === 'vessel' ? 'vessel' : 'req')}
        items={[
          { key: 'req', label: `Yêu cầu ${viewN}` },
          { key: 'vessel', label: `Tàu ${vesselN}` },
        ]}
      />
      <div className={s.coord} role="group" aria-label={t('booking.coordTip')}>
        <span className={s.av} aria-hidden="true">
          {initials(ME)}
        </span>
        <span className={s.coordTx}>
          {t('booking.coord')}{' '}<b>{ME}</b>
        </span>
        <Button size="sm" variant="ghost" icon={<IconCalendar size={14} aria-hidden="true" />} title={t('booking.scheduleTip')} onClick={noWrite}>
          {t('booking.schedule')}
        </Button>
        <Button size="sm" variant="ghost" icon={<IconUsersGroup size={14} aria-hidden="true" />} title={t('booking.coordTip')} onClick={noWrite}>
          {t('booking.dispatch')}
        </Button>
      </div>
    </div>
  );

  const formRow = form && state === 'ready' ? all.find((x) => x.id === form) : undefined;
  if (formRow) {
    return (
      <Page kind="table">
        <BookingTwoForm r={formRow} onBack={() => setForm(null)} onWrite={noWrite} />
      </Page>
    );
  }

  const detailRow = padLayout && detail ? all.find((x) => x.id === detail) : undefined;
  if (detailRow && state === 'ready') {
    return (
      <Page kind="table">
        <BookingPadDetail r={detailRow} onBack={() => setDetail(null)} onWrite={noWrite} onOpenJob={(href) => void navigate({ to: href })} />
      </Page>
    );
  }

  const phoneRow = phoneLayout && detail ? all.find((x) => x.id === detail) : undefined;
  if (phoneRow && state === 'ready') {
    return (
      <Page kind="table">
        <BookingPhoneDetail r={phoneRow} onBack={() => setDetail(null)} onWrite={noWrite} />
      </Page>
    );
  }

  if (padLayout || phoneLayout) {
    const View = phoneLayout ? BookingPhone : BookingPad;
    return (
      <Page kind="table">
        <View
          me={ME}
          state={state}
          scope={scope}
          onScope={setScope}
          ownCounts={ownCounts}
          tab={tabEff}
          counts={counts}
          lock={lock}
          onTab={pick}
          q={q}
          onQ={setQ}
          groups={groups}
          ack={ack}
          onAck={(code) => {
            setAck((a) => ({ ...a, [code]: true }));
            toast({ msg: `Đã nhận việc khẩn ${code} · đã báo Sale của job` });
          }}
          inboxN={6}
          onOpen={open}
          onVessel={setVesselOf}
          onWrite={noWrite}
          onRetry={() => void refetch()}
        />
        <VesselDrawer vessel={vesselOf ? (profiles.get(vesselId(vesselOf)) ?? vesselFromName(vesselOf)) : null} onClose={() => setVesselOf(null)} />
      </Page>
    );
  }

  return (
    <Page kind="table">
      <PageHeader
        title={t('booking.a11y1')}
        tabs={desktop ? viewTabs : undefined}
        actions={
          <>
            {desktop && (
              <>
                <HeaderTool label="Tải BKG" icon={IconDownload} onClick={noWrite} />
                <HeaderTool label="Việc" icon={IconChecklist} n={5} />
                <HeaderTool label="Thông báo" icon={IconBell} n={2} />
                <HeaderTool label="Thao tác khác" icon={IconDots} onClick={noWrite} />
                <Button variant="secondary" icon={<IconSend size={16} aria-hidden="true" />} title={t('booking.a11y2')} onClick={noWrite}>
                  {t('booking.bulkRod')}
                </Button>
              </>
            )}
            {desktop && (
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
                {t('booking.newReq')}
              </Button>
            )}
          </>
        }
      />
      <div className={`${s.body} ${desktop ? '' : s.bodyOne} ${inbox ? '' : s.bodyRail} ${showDock ? s.bodyDock : ''}`}>
        {desktop && <InboxPane me={ME} requests={all} open={inbox} onToggle={() => setInbox((v) => !v)} onJob={(j) => setQ(j)} onNew={noWrite} onWrite={noWrite} />}
        <section className={s.center} aria-label={t('booking.a11y3')}>
          <div className={s.page}>
            {view === 'vessel' && ready ? (
              <VesselView requests={all} onProfile={setVesselOf} onOpenReq={open} onInRequests={() => setView('req')} onWrite={noWrite} />
            ) : (
              <>
            <div className={s.bar}>
              <div className={s.tabs}>
                <div role="tablist" aria-label={t('booking.a11y4')} className={s.tabList}>
                  {RL_TABS.filter((t) => RL_MAIN.includes(t.key)).map((t) => tabBtn(t.key, t.label, false))}
                  <div className={s.more}>
                    <button type="button" className={`${s.tabBtn} ${inMore ? s.on : ''}`} aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
                      {inMore ? `${tabLabel} ${counts[tabEff]}` : 'Thêm'}
                      <IconChevronDown size={12} aria-hidden="true" />
                    </button>
                    {moreOpen && (
                      <div role="menu" aria-label={t('booking.a11y5')} className={s.menu}>
                        {RL_TABS.filter((t) => !RL_MAIN.includes(t.key)).map((t) => tabBtn(t.key, t.label, true))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={s.tools}>
                <label className={s.search}>
                  <IconSearch size={15} aria-hidden="true" />
                  <input aria-label={t('booking.a11y6')} placeholder="BookingNo / JobID / HBL / MBL" value={q} onChange={(e) => setQ(e.target.value)} />
                  {q && (
                    <button type="button" className={s.clear} aria-label={t('booking.a11y7')} onClick={() => setQ('')}>
                      ×
                    </button>
                  )}
                </label>
                {desktop && (
                  <>
                    <Button variant="soft" icon={<IconCalendarEvent size={15} aria-hidden="true" />} title={t('booking.a11y8')} onClick={noWrite}>
                      JobDate
                    </Button>
                    <Button variant="soft" icon={<IconFilter size={15} aria-hidden="true" />} onClick={noWrite}>
                      {t('booking.filters')}
                    </Button>
                  </>
                )}
                <Segmented
                  kind="toggle"
                  label="Booking Staff"
                  value={scope}
                  onChange={setScope}
                  items={SCOPES.map((x) => ({ key: x.key, label: x.label, n: ownCounts[x.key] }))}
                />
                {desktop && <button type="button" role="switch" aria-checked={byVessel} className={s.sw} title={t('booking.a11y9')} onClick={() => setByVessel((v) => !v)}>
                  <span className={`${s.swTrack} ${byVessel ? s.swOn : ''}`}>
                    <span className={s.swKnob} />
                  </span>
                  {t('booking.byVessel')}
                </button>}
              </div>
            </div>
            {ready && prioN > 0 && !q && (
              <div role="alert" className={s.prio}>
                <span>
                  <b>Ưu tiên {prioN}</b> {t('booking.prioNote')}
                </span>
              </div>
            )}
            {ready && desktop && (
              <section aria-label={t('booking.a11y10')} className={s.p0s}>
                {BOOKING_P0.map((p) => {
                  const done = !!ack[p.code];
                  const step = p.minutes >= 60 ? 2 : p.minutes >= 30 ? 1 : 0;
                  const who = BOOKING_COVER[p.pic] ? `${BOOKING_COVER[p.pic]} (thay ${p.pic})` : p.pic;
                  return (
                    <div key={p.code} className={`${s.p0} ${done ? s.p0Ack : ''}`}>
                      <span className={s.p0Code} title={t('booking.a11y11')}>
                        P0 · {p.code}
                      </span>
                      <div className={s.p0Mid}>
                        <div className={s.p0Tx}>
                          <b>{p.k}</b> <span>· {p.ref}</span>
                        </div>
                        <div role="list" aria-label="Leo thang" className={s.esc} title={`Push P0 có âm báo, không tự tắt · 0′ ${who} → 30′ Sale ${p.sale} → 60′ Trưởng phòng Sales · dự phòng Bot Zalo nội bộ · không leo thang sang Ops`}>
                          {[
                            ['0′', who],
                            ['30′', `Sale ${p.sale}`],
                            ['60′', 'Trưởng phòng Sales'],
                          ].map(([tt, w], j) => (
                            <span key={tt} role="listitem" className={`${s.escStep} ${!done && j === step ? s.escOn : ''}`}>
                              <span className={s.escDot} style={{ background: !done && j <= step ? 'var(--err)' : 'var(--bd)' }} />
                              {tt} {w}
                            </span>
                          ))}
                        </div>
                      </div>
                      {!done && (
                        <span className={s.p0El} title={t('booking.a11y12')} style={{ color: step ? 'var(--err)' : 'var(--tx2)' }}>
                          {p.minutes}′
                        </span>
                      )}
                      <span className={s.p0Left} title={`Còn lại ${p.left}`}>
                        <IconClock size={14} aria-hidden="true" />
                        {p.left}
                      </span>
                      {done ? (
                        <span className={s.p0Done}>Đã nhận · {ME} 09:42</span>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={() => {
                            setAck((a) => ({ ...a, [p.code]: true }));
                            toast({ msg: `Đã nhận việc khẩn ${p.code} · đã báo Sale của job` });
                          }}
                        >
                          {t('booking.mine')}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </section>
            )}
            {ready && desktop && (
              <section aria-label={t('booking.a11y13')} className={s.today}>
                <b>{t('booking.today')}</b>
                {todayStrip(counts).map((d) => (
                  <button key={d.code} type="button" className={s.todayBtn} title={`Mở tab ${d.k}`} onClick={() => pick(d.go)}>
                    <span className={s.todayCode}>{d.code}</span>
                    {d.k}
                    <b style={{ color: d.tone === 'link' ? 'var(--link)' : d.tone === 'warn' ? 'var(--warn)' : 'var(--lb)' }}>{d.n}</b>
                  </button>
                ))}
              </section>
            )}
            <StateView
              state={ready && rows.length === 0 ? 'empty' : state}
              title={ready ? 'Không có yêu cầu khớp' : state === 'empty' ? 'Chưa có yêu cầu booking' : undefined}
              desc={ready ? 'Thử đổi nhóm, phạm vi hoặc bỏ từ khoá tìm.' : undefined}
              icon={ready || state === 'empty' ? <IconAnchor size={26} stroke={1.8} /> : undefined}
              action={state === 'error' ? <Button onClick={() => void refetch()}>{t('booking.retry')}</Button> : undefined}
              skeleton={<Skeleton rows={8} />}
            >
              <div className={s.groups}>
                {groups.map((g) => {
                  const v = g.vessel ? BOOKING_VESSELS[g.vessel] : undefined;
                  if (g.vessel && !v) return null;
                  const closed = !!shut[g.vessel];
                  const needNotify = !!v?.change && g.rows.some((r) => !r.notified);
                  return (
                    <section key={g.vessel || 'flat'} className={s.group} aria-label={v?.name ?? 'Danh sách'}>
                      {v && (
                        <div className={s.head}>
                          <div className={s.headRow}>
                            <IconShip size={16} aria-hidden="true" />
                            <button type="button" className={s.vsl} onClick={() => setShut((o) => (closed ? Object.fromEntries(Object.entries(o).filter(([k]) => k !== g.vessel)) : { ...o, [g.vessel]: true }))}>
                              {v.name}
                            </button>
                            <span className={s.meta}>{v.line}</span>
                            {g.vessel !== 'none' && (
                              <>
                                {([['SI', g.rows.find((r) => r.siCutoff)?.siCutoff, 'Doc cut-off · đếm ngược theo giờ hãng'], ['CY', g.rows.find((r) => r.cyCutoff)?.cyCutoff, `Closing CY${v.change ? ` · đổi từ ${v.change.from}` : ''}`]] as const).map(([lab, val, tip]) => {
                                  const cd = countdown(val ?? null, false);
                                  const tone = cd && cd.mins < 0 ? s.chipErr : cd && cd.mins < 1440 ? s.chipWarn : '';
                                  return (
                                    <span key={lab} className={`${s.cutChip} ${tone}`} title={tip}>
                                      <IconAlarm size={12} aria-hidden="true" />
                                      {`${lab} ${val ?? '—'}${cd ? ` · ${cd.k}` : ''}`}
                                    </span>
                                  );
                                })}
                              </>
                            )}
                            <span className={s.count}>{g.rows.length} yêu cầu</span>
                            {g.vessel !== 'none' && (
                              <button type="button" className={s.vlinkBtn} data-vlink={vesselId(v.name)} title={t('booking.a11y14')} onClick={() => setVesselOf(v.name)}>
                                <IconShip size={14} aria-hidden="true" />
                                {t('booking.viewVessel')}
                              </button>
                            )}
                            <button
                              type="button"
                              className={s.fold}
                              aria-label={`Thu hoặc mở nhóm ${v.name}`}
                              title={`${closed ? 'Mở' : 'Thu'} nhóm ${v.name}`}
                              aria-expanded={!closed}
                              onClick={() => setShut((o) => (closed ? Object.fromEntries(Object.entries(o).filter(([k]) => k !== g.vessel)) : { ...o, [g.vessel]: true }))}
                            >
                              <IconChevronDown size={14} aria-hidden="true" style={{ transform: closed ? undefined : 'rotate(180deg)' }} />
                            </button>
                          </div>
                          {v.change && (
                            <div className={s.chg}>
                              <span className={s.chgK}>{t('booking.reschedule')}</span>
                              <span className={s.chgT}>
                                ETD <s>{v.change.from}</s> → <b>{v.change.to}</b> · {v.change.why}
                              </span>
                              {needNotify && (
                                <Button size="sm" variant="secondary" className={s.chgBtn} onClick={noWrite}>
                                  {t('booking.notifyRest')}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {!closed && g.rows.map((r) => <Row key={r.id} r={r} onOpen={open} onMore={noWrite} onClaim={noWrite} />)}
                    </section>
                  );
                })}
              </div>
            </StateView>
            {ready && desktop && (
              <div className={s.foot}>
                <Button size="sm" variant="ghost" icon={<IconRefresh size={14} aria-hidden="true" />} onClick={() => void refetch()}>
                  {t('booking.refresh')}
                </Button>
                <Button size="sm" variant="ghost" icon={<IconKeyboard size={14} aria-hidden="true" />} onClick={() => toast({ msg: 'Phím tắt: [ thu hộp thư · ] mở hộp thư' })}>
                  {t('booking.shortcuts')}
                </Button>
              </div>
            )}
              </>
            )}
          </div>
        </section>
        {showDock && <Dock rows={scoped} onPick={(t) => setTab(t === 'queue' ? 'rqnew' : t === 'doing' ? 'rqproc' : t)} onClose={() => setDock(false)} />}
      </div>
      <VesselDrawer vessel={vesselOf ? (profiles.get(vesselId(vesselOf)) ?? vesselFromName(vesselOf)) : null} onClose={() => setVesselOf(null)} />
    </Page>
  );
}

const vesselId = (name: string) => name.replace(/\s(\S+)$/, '|$1');

/** Chưa có hồ sơ tàu trong dữ liệu `vessel`: dựng từ tên tàu + chuyến của nhóm (chưa có IMO). */
function vesselFromName(name: string): Vessel {
  const [n = '', voyage = ''] = vesselId(name).split('|');
  return { id: vesselId(name), name: n, voyage, carrier: '', service: null, terminal: '', pol: '', noticeType: '', etd: '', cy: '', icd: '', si: '', atd: '', remark: '', changes: 0, profile: null };
}

function Row({ r, onOpen, onMore, onClaim }: { r: BookingRequest; onOpen: (r: BookingRequest) => void; onMore: () => void; onClaim: () => void }) {
  const t = useT();
  const ship = shipStatus(r);
  const cut = rowCutoff(r);
  return (
    <div
      className={s.row}
      role="button"
      tabIndex={0}
      aria-label={`Mở ${r.jobId} · 2 form`}
      onClick={() => onOpen(r)}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen(r);
        }
      }}
    >
      <input type="checkbox" className={s.check} aria-label={`Chọn ${r.jobId}`} onClick={(e) => e.stopPropagation()} onChange={() => undefined} />
      <div className={s.l1}>
        <b className={s.job}>{r.jobId}</b>
        <span className={`${s.ship} ${s[`ship_${ship.dot}`]}`} title="ShipmentStatus">
          <span className={s.shipDot} />
          {ship.k}
        </span>
        <span className={s.rq} title="RQStatus">{rqStatus(r)}</span>
        {r.sourceStatus && (
          <span className={`${s.pill} ${s.pillErr}`} title={SOURCE[r.sourceStatus].tip}>
            {SOURCE[r.sourceStatus].k}
          </span>
        )}
        {r.flags.map((f) => (
          <span key={f} className={`${s.pill} ${FLAG[f].cls ?? ''}`} title={FLAG[f].tip}>
            {FLAG[f].k}
          </span>
        ))}
      </div>
      <div className={s.r1}>
        {cut && (
          <span className={`${s.cd} ${s[`cd_${cut.cd.tone}`]}`} title={cut.tip}>
            <IconAlarm size={12} aria-hidden="true" />
            {`${cut.label} ${cut.cd.k}`}
          </span>
        )}
        {r.owner ? (
          <span className={s.own} title={`Đang xử lý: ${r.owner}`}>
            <span className={s.av} aria-hidden="true">
              {initials(r.owner)}
            </span>
            {r.owner}
          </span>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            title={t('booking.a11y15')}
            onClick={(e) => {
              e.stopPropagation();
              onClaim();
            }}
          >
            {t('booking.claim')}
          </Button>
        )}
        <button
          type="button"
          className={s.dots}
          aria-label={`Thao tác ${r.jobId}`}
          title={t('booking.a11y16')}
          onClick={(e) => {
            e.stopPropagation();
            onMore();
          }}
        >
          <IconDots size={16} aria-hidden="true" />
        </button>
      </div>
      <div className={s.l2}>
        <b className={s.cus}>{r.customer}</b>
        <span title="BookingNo" className={r.bookingNo ? undefined : s.warnTx}>
          {r.bookingNo ?? 'Chưa có booking'}
        </span>
        <span title={t('booking.a11y17')}>{r.cont}</span>
        <span title={t('booking.a11y18')}>{`${r.carrier} · ${carrierChannel(r.carrier)}`}</span>
        <span title="ETD">ETD {BOOKING_VESSEL_ETD[r.vessel] ?? '—'}</span>
        <span title={t('booking.a11y19')}>{r.route}</span>
        {r.remark && (
          <span className={s.rem} title="Internal Remark">
            “{r.remark}”
          </span>
        )}
      </div>
    </div>
  );
}
